/**
 * 导演模式前端服务层（用户接口）。
 *
 * 与 services/api.ts 同级独立模块，避免污染主图片业务的 namespace。
 * 所有方法返回类型化数据；错误统一抛 DirectorApiError，路由层（页面）负责 toast 提示。
 */

import { PlatformApiError, platformFetch, readEnvelope, readSSEStream } from '../../../platform/http';

// ============================================================================
// 错误类型
// ============================================================================

/** 继承平台统一错误，保留导演模块的历史名称与构造签名，消费方零改动 */
export class DirectorApiError extends PlatformApiError {
  constructor(message: string, status: number, code?: string, meta?: Record<string, unknown>) {
    super(message, status, code, meta);
    this.name = 'DirectorApiError';
  }
}

// ============================================================================
// fetch 封装
// ============================================================================

async function authFetch<T = unknown>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  try {
    return await readEnvelope<T>(
      await platformFetch(path, options),
      (status) => `请求失败 (${status})`
    );
  } catch (error) {
    if (error instanceof PlatformApiError && !(error instanceof DirectorApiError)) {
      throw new DirectorApiError(error.message, error.status, error.code, error.meta);
    }
    throw error;
  }
}

/**
 * multipart/form-data 上传专用：与 authFetch 同样的错误 / data 解析，
 * platformFetch 检测到 FormData body 时自动不设 Content-Type（保留 boundary）
 */
async function authFetchFormData<T = unknown>(path: string, formData: FormData): Promise<T> {
  try {
    return await readEnvelope<T>(
      await platformFetch(path, { method: 'POST', body: formData }),
      (status) => `上传失败 (${status})`
    );
  } catch (error) {
    if (error instanceof PlatformApiError && !(error instanceof DirectorApiError)) {
      throw new DirectorApiError(error.message, error.status, error.code);
    }
    throw error;
  }
}

// ============================================================================
// 类型（与后端 prisma 模型对齐，前端只列展示需要的字段）
// ============================================================================

export type DirectorProjectStatus =
  | 'draft'
  | 'producing'
  | 'ready'
  | 'composing'
  | 'completed'
  | 'archived';

/**
 * 项目模式：仅作为「新建分镜」时的默认 shot.mode
 * - "video_per_shot"：新分镜默认为 keyframe
 * - "image_sequence"：新分镜默认为 frame_sequence
 *
 * 真实决定一个分镜行为的是 DirectorShot.mode，每个分镜独立。
 */
export type DirectorProjectMode = 'video_per_shot' | 'image_sequence' | 'trinity_cards';

/**
 * 分镜模式（决定该分镜采用哪种"图 → 视频"管线）：
 * - "keyframe"：单首帧 keyframe → img2video
 * - "frame_sequence"：N 张帧 → 视频模型多参考图
 */
export type DirectorShotMode = 'keyframe' | 'frame_sequence' | 'trinity_cards';

export type TrinityCardSlot = 'role' | 'story' | 'video';
export type TrinityCardsTaskStatus =
  | 'pending'
  | 'running'
  | 'awaiting_confirm'
  | 'completed'
  | 'failed'
  | 'cancelled';
export type TrinityCardsTaskStage =
  | 'queued'
  | 'generating_prompts'
  | 'creating_jobs'
  | 'syncing_cards'
  | 'completed'
  | 'failed';

export interface TrinityCardsDraftPrompts {
  role: string | null;
  story: string | null;
  video: string | null;
}

/** 三合一卡候选图（历史生成的图，可锁定回退） */
export interface TrinityCardCandidate {
  id: string;
  imageJobId: string;
  imageUrl: string | null;
  thumbnailUrl: string | null;
  status: 'pending' | 'submitting' | 'processing' | 'completed' | 'failed';
  createdAt: string;
}

export interface TrinityCardItem {
  slot: TrinityCardSlot;
  label: string;
  sortOrder: number;
  imageJobId: string | null;
  imageUrl: string | null;
  thumbnailUrl: string | null;
  prompt: string | null;
  status:
    | 'empty'
    | 'draft'
    | 'waiting_dependency'
    | 'pending'
    | 'submitting'
    | 'processing'
    | 'completed'
    | 'failed';
  /** 该 slot 的历史候选图（不含当前活跃图）；项目列表摘要中可能缺 */
  candidates?: TrinityCardCandidate[];
  /** 当前活跃图的 imageJobId；项目列表摘要中可能缺 */
  lockedImageJobId?: string | null;
}

export interface TrinityCardsTask {
  taskId: string;
  shotId: string;
  targetSlot: TrinityCardSlot | null;
  status: TrinityCardsTaskStatus;
  stage: TrinityCardsTaskStage;
  message: string;
  startedAt: string;
  completedAt: string | null;
  alreadyRunning: boolean;
  draftPrompts: TrinityCardsDraftPrompts | null;
  imageCreditsEstimate: number | null;
  aiCreditsUsed: number;
  /** true = 级联生成，false = 单卡独立生成 */
  cascade: boolean;
  /** LLM 流式输出累计文本（仅 status='running' 阶段有值） */
  streamingText: string;
  /** 流式过程中尽力解析出的"半成品"三段（未完成的为 null） */
  streamingPartial: { role: string | null; story: string | null; video: string | null };
  result: {
    aiCreditsUsed: number;
    imageCreditsUsed: number;
    creditsUsed: number;
    jobs: Array<{ slot: TrinityCardSlot; label: string; jobId: string }>;
  } | null;
  error: { message: string; code?: string; status?: number } | null;
}

export interface TrinityCardsGeneratePreviewItem {
  slot: TrinityCardSlot;
  label: string;
  estimatedCredits: number;
  mode: 'text2img' | 'img2img';
  referenceCount: number;
}

export type TrinityReferenceAssetMissingReason =
  | 'asset_not_found'
  | 'missing_image'
  | 'image_not_completed';

export interface TrinityResolvedReferenceAssetItem {
  id: string;
  name: string;
  referenceIndex: number;
}

export interface TrinityMissingReferenceAssetItem {
  id: string;
  name: string;
  reason: TrinityReferenceAssetMissingReason;
}

export interface TrinityReferenceAssetResolution {
  actual: {
    characters: TrinityResolvedReferenceAssetItem[];
    props: TrinityResolvedReferenceAssetItem[];
    scene: TrinityResolvedReferenceAssetItem | null;
    totalCount: number;
  };
  missing: {
    characters: TrinityMissingReferenceAssetItem[];
    props: TrinityMissingReferenceAssetItem[];
    scene: TrinityMissingReferenceAssetItem | null;
  };
}

export interface TrinityCardsGeneratePreview {
  shotId: string;
  targetSlot: TrinityCardSlot | null;
  modelId: string;
  quality: 'low' | 'medium' | 'high';
  currentCredits: number;
  aiCredits: number;
  imageCredits: number;
  totalCredits: number;
  cards: TrinityCardsGeneratePreviewItem[];
  referenceAssets: TrinityReferenceAssetResolution;
}

export interface TrinityCardsGenerateInput {
  prompt: string;
  injectedCharacterIds: string[];
  injectedPropIds: string[];
  injectedSceneId: string | null;
  modelId?: string;
  quality?: 'low' | 'medium' | 'high';
}

export interface DirectorProject {
  id: string;
  userId: string;
  name: string;
  description: string | null;
  aspectRatio: string;
  /** 项目级清晰度（应用到所有分镜的视频生成） */
  resolution: '480p' | '720p' | '1080p';
  videoModelId: string;
  imageModelId: string | null;
  mode: DirectorProjectMode;
  status: DirectorProjectStatus;
  isArchived: boolean;
  shotCount: number;
  lastCompositionId: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * 「兼容导演模式」的视频模型简表（来自 /director/video-models）
 *
 * 后端只会返回：
 * 1. 模型至少有一个有效的 img2video 渠道（默认渠道或专属渠道）
 * 2. 该渠道 provider 声明支持 img2video
 * 3. provider 至少有一个真实分辨率（不全为 "-"）
 *
 * `availableAspectRatios` / `availableResolutions` 是该模型 img2video 渠道
 * provider 在 metadata 中声明的实际可选项；前端应据此过滤可选 UI。
 */
export interface DirectorCompatibleVideoModel {
  modelId: string;
  name: string;
  description: string | null;
  availableAspectRatios: string[];
  availableResolutions: string[];
  /** 是否支持首尾帧（独立 /video 工作台用；导演模式不再使用） */
  supportsFirstLastFrame: boolean;
}

export interface DirectorProjectWithCover extends DirectorProject {
  coverImageJobId: string | null;
  coverUrl: string | null;
  /** 项目所选视频模型的人话名称（list 接口富化）；模型已被删除时为 null */
  videoModelName: string | null;
}

export interface DirectorShot {
  id: string;
  projectId: string;
  shotOrderInProject: number;
  /** 分镜模式：keyframe（单首帧）/ frame_sequence（多帧）/ trinity_cards（固定三卡） */
  mode: DirectorShotMode;
  title: string | null;
  prompt: string;
  duration: number | null;
  motionIntensity: string | null;
  cameraMovement: string | null;
  lockedKeyframeImageJobId: string | null;
  injectedCharacterIds: string | null;
  injectedPropIds: string | null;
  injectedSceneId: string | null;
  /** 当前激活的视频 Job ID。null 表示无激活视频；候选池里可能仍有历史视频 */
  activeVideoJobId: string | null;
  contextStaleHint: boolean;
  /** JSON 字符串，反序列化后为 CharacterLayoutItem[] */
  characterLayout: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CharacterLayoutItem {
  characterId: string;
  x: number;
  y: number;
  scale: number;
}

/**
 * 富化的分镜数据：getProject 返回时附带锁定 keyframe 的可访问 URL，
 * 以及当前激活 VideoJob 的状态（用户视角，不包含 errorMessage）。
 */
export interface DirectorShotEnriched extends DirectorShot {
  lockedKeyframeUrl: string | null;
  lockedKeyframeThumbnailUrl: string | null;
  /** 帧序列长度（image_sequence 模式专用，video_per_shot 模式恒为 0） */
  frameCount?: number;
  /** 帧序列首帧的可访问 URL（image_sequence 模式专用） */
  firstFrameUrl?: string | null;
  /** 三合一卡已完成数量（0~3） */
  trinityCardCount?: number;
  /** 三合一卡封面（默认取第一个已完成卡） */
  trinityCoverUrl?: string | null;
  /** 三合一卡详细列表（固定 3 项） */
  trinityCards?: TrinityCardItem[];
  activeVideoJob: {
    id: string;
    status: 'queued' | 'pending' | 'submitting' | 'processing' | 'completed' | 'failed' | 'timeout';
    videoUrl: string | null;
    /** 该 VideoJob 当时使用的视频模型人话名（VideoJob.model 快照解析得到）；模型已删除时回退为 modelId 字符串 */
    modelName: string | null;
  } | null;
  /**
   * 历史候选视频列表（按 createdAt desc）。
   * 取消激活的 completed 视频不再永久消失，而是落入候选池供用户切换回来。
   * 每分镜最多 MAX_VIDEO_CANDIDATES_PER_SHOT 条（后端常量，超出最旧的会被软删）。
   */
  candidateVideos: Array<{
    id: string;
    videoUrl: string | null;
    coverUrl: string | null;
    /** 视频实际时长（秒，浮点）；null 表示未知 */
    resultDuration: number | null;
    /** 用户配置时长（秒，整数） */
    duration: number;
    createdAt: string;
  }>;
}

export interface DirectorCharacter {
  id: string;
  userId: string;
  name: string;
  description: string | null;
  portraitImageJobId: string | null;
  additionalAngles: string | null;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
  /** 项目接口附带：portrait 的可访问 URL（getProject 时填充；listCharacters 不填） */
  portraitUrl?: string | null;
  /** 同上，缩略图（缺失时回退 portraitUrl） */
  portraitThumbnailUrl?: string | null;
}

export interface DirectorScene {
  id: string;
  /**
   * 场景所属用户。场景已升级为用户级（与角色 / 物品一致），跨项目共享。
   * 旧字段 `projectId` 已废弃 —— 想知道该场景目前在哪些项目里使用，请通过项目接口反查。
   */
  userId: string;
  name: string;
  description: string | null;
  settingImageJobId: string | null;
  /** 多视角衍生（JSON 字符串 / null），与 character / prop 对齐 */
  additionalAngles: string | null;
  isArchived: boolean;
  /** 仅在通过 listScenes(projectId) / getProject 拉取时存在；表示该场景在该项目内的排序 */
  sortOrder?: number;
  createdAt: string;
  updatedAt: string;
  /** 项目接口附带：场景设定图的可访问 URL（getProject 时填充；listScenes 不填） */
  settingUrl?: string | null;
  /** 同上，缩略图（缺失时回退 settingUrl） */
  settingThumbnailUrl?: string | null;
}

export interface DirectorProp {
  id: string;
  userId: string;
  name: string;
  description: string | null;
  portraitImageJobId: string | null;
  additionalAngles: string | null;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
  /** 项目接口附带：portrait 的可访问 URL（getProject 时填充；listProps 不填） */
  portraitUrl?: string | null;
  portraitThumbnailUrl?: string | null;
}

export interface DirectorComposition {
  id: string;
  projectId: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  transitionType: 'none' | 'fade';
  transitionDurationMs: number;
  outputResolution: string;
  aspectRatio: string;
  watermarkEnabled: boolean;
  audioNormalizeEnabled: boolean;
  shotsSnapshot: string;
  shotsTimingSnapshot: string | null;
  resultStorageKey: string | null;
  resultStorageType: string | null;
  resultDuration: number | null;
  resultFileSize: number | null;
  isObsolete: boolean;
  /** 后端解析后的可访问 URL（公有桶为公开 URL，私有桶为短期签名 URL）。null 表示尚未生成或失败 */
  resultUrl: string | null;
  createdAt: string;
  updatedAt: string;
  startedAt: string | null;
  completedAt: string | null;
}

export interface DirectorShare {
  id: string;
  projectId: string;
  compositionId: string;
  shareToken: string;
  allowDownload: boolean;
  expiresAt: string | null;
  isRevoked: boolean;
  viewCount: number;
  lastViewedAt: string | null;
  createdAt: string;
}

// ImageJob（导演 keyframe 用）— 与后端字段保持一致，前端只列需要的
export interface DirectorImageJob {
  id: string;
  userId: string;
  status: 'pending' | 'submitting' | 'processing' | 'completed' | 'failed';
  prompt: string;
  resultUrl: string | null;
  thumbnailUrl: string | null;
  width: number | null;
  height: number | null;
  directorShotId: string | null;
  usageRole: string | null;
  isHidden: boolean;
  createdAt: string;
}

export interface ShotProposal {
  title?: string;
  prompt: string;
  suggestedDuration?: number;
  suggestedCameraMovement?: string;
  suggestedMotionIntensity?: string;
  suggestedCharacterIds?: string[];
}

export interface ActivateAllPreview {
  toActivateCount: number;
  skippedActiveCount: number;
  skippedNoKeyframeCount: number;
  estimatedCreditsTotal: number;
}

export interface DirectorPromptTemplate {
  label: string;
  text: string;
}

export interface DirectorPromptTemplateGroup {
  groupLabel: string;
  groupHint?: string;
  templates: DirectorPromptTemplate[];
}

// ============================================================================
// API
// ============================================================================

export const directorApi = {
  // ---------------- 公开配置 ----------------

  /**
   * 拿一份"用户可见的"导演模式配置（如水印是否开启），用于驱动 UI 显隐
   * spec 16.6：站点水印关闭时，PreviewMode 拼接设置中的水印开关也消失
   */
  getPublicSettings() {
    return authFetch<{
      siteWatermarkEnabled: boolean;
      maxShotsPerProject: number;
      maxKeyframeCandidatesPerUser: number;
      shareLinkDefaultExpiresInDays: number;
      shotSplitCreditsPerCall: number;
      shotSplitLlmEnabled: boolean;
      frameStoryboardCreditsPerCall: number;
      frameStoryboardLlmEnabled: boolean;
      trinityCardsCreditsPerCall: number;
      trinityCardsLlmEnabled: boolean;
      keyframeEnabled: boolean;
      frameSequenceEnabled: boolean;
      trinityCardsEnabled: boolean;
      promptTemplateGroups: DirectorPromptTemplateGroup[];
    }>('/director/public-settings');
  },

  // ---------------- 项目 ----------------

  listProjects(params: {
    filter?: 'all' | 'active' | 'archived';
    page?: number;
    pageSize?: number;
  } = {}) {
    const query = new URLSearchParams();
    if (params.filter) query.set('filter', params.filter);
    if (params.page) query.set('page', String(params.page));
    if (params.pageSize) query.set('pageSize', String(params.pageSize));
    return authFetch<{
      data: DirectorProjectWithCover[];
      pagination: { page: number; pageSize: number; total: number };
    }>(`/director/projects?${query.toString()}`);
  },

  createProject(input: {
    name: string;
    description?: string;
    aspectRatio: '16:9' | '9:16' | '1:1';
    resolution: '480p' | '720p' | '1080p';
    videoModelId: string;
    imageModelId?: string;
    mode?: DirectorProjectMode;
  }) {
    return authFetch<DirectorProject>('/director/projects', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /**
   * 列出可用于导演模式的视频模型（按 img2video 能力过滤）。
   * 每个模型附带其 img2video 渠道 provider 实际声明的比例 / 分辨率枚举。
   * 前端用来联动比例 / 清晰度选择 UI 与能力提示。
   */
  listCompatibleVideoModels() {
    return authFetch<DirectorCompatibleVideoModel[]>('/director/projects/video-models');
  },

  getProject(projectId: string) {
    return authFetch<
      DirectorProject & {
        shots: DirectorShotEnriched[];
        scenes: DirectorScene[];
        projectCharacters: Array<{ characterId: string; sortOrder: number; character: DirectorCharacter }>;
        projectProps: Array<{ propId: string; sortOrder: number; prop: DirectorProp }>;
      }
    >(`/director/projects/${projectId}`);
  },

  updateProject(
    projectId: string,
    patch: {
      name?: string;
      description?: string | null;
      imageModelId?: string | null;
      videoModelId?: string;
    }
  ) {
    return authFetch<DirectorProject>(`/director/projects/${projectId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },

  /** 切换视频模型前的兼容性预检（dry-run，不写库） */
  previewVideoModelChange(projectId: string, videoModelId: string) {
    return authFetch<{
      currentModelId: string;
      currentModelName: string | null;
      newModelId: string;
      newModelName: string;
      aspectCompatible: boolean;
      resolutionCompatible: boolean;
      newSupportedAspectRatios: string[];
      newSupportedResolutions: string[];
      newMaxReferenceImageCount: number;
      newDurationLabel: string;
      incompatibleShots: Array<{
        shotId: string;
        shotOrderInProject: number;
        title: string | null;
        reasons: string[];
      }>;
    }>(`/director/projects/${projectId}/preview-video-model-change`, {
      method: 'POST',
      body: JSON.stringify({ videoModelId }),
    });
  },

  duplicateProject(projectId: string) {
    return authFetch<DirectorProject>(`/director/projects/${projectId}/duplicate`, {
      method: 'POST',
    });
  },

  archiveProject(projectId: string) {
    return authFetch<void>(`/director/projects/${projectId}/archive`, { method: 'POST' });
  },
  unarchiveProject(projectId: string) {
    return authFetch<void>(`/director/projects/${projectId}/unarchive`, { method: 'POST' });
  },
  hardDeleteProject(projectId: string) {
    return authFetch<void>(`/director/projects/${projectId}`, { method: 'DELETE' });
  },
  getProjectDeletePreview(projectId: string) {
    return authFetch<{
      shotCount: number;
      keyframeCount: number;
      videoCount: number;
      compositionCount: number;
      sceneCount: number;
    }>(`/director/projects/${projectId}/delete-preview`);
  },

  // ---------------- 分镜 ----------------

  createShot(projectId: string, initial?: { title?: string; prompt?: string; duration?: number; cameraMovement?: string; motionIntensity?: string; injectedCharacterIds?: string[]; injectedSceneId?: string }) {
    return authFetch<DirectorShot>(`/director/projects/${projectId}/shots`, {
      method: 'POST',
      body: JSON.stringify(initial ?? {}),
    });
  },
  updateShot(shotId: string, patch: Partial<{
    title: string | null;
    prompt: string;
    duration: number | null;
    motionIntensity: string | null;
    cameraMovement: string | null;
    injectedCharacterIds: string[] | null;
    injectedPropIds: string[] | null;
    injectedSceneId: string | null;
    characterLayout: CharacterLayoutItem[] | null;
  }>) {
    return authFetch<DirectorShot>(`/director/shots/${shotId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },
  moveShotUp(shotId: string) {
    return authFetch<void>(`/director/shots/${shotId}/move-up`, { method: 'POST' });
  },
  moveShotDown(shotId: string) {
    return authFetch<void>(`/director/shots/${shotId}/move-down`, { method: 'POST' });
  },
  /** 故事板拖拽重排：一次性按顺序重写整个项目分镜顺序 */
  reorderShots(projectId: string, orderedShotIds: string[]) {
    return authFetch<void>(`/director/projects/${projectId}/shots/reorder`, {
      method: 'POST',
      body: JSON.stringify({ orderedShotIds }),
    });
  },
  duplicateShot(shotId: string) {
    return authFetch<DirectorShot>(`/director/shots/${shotId}/duplicate`, { method: 'POST' });
  },
  reuseShotKeyframe(shotId: string) {
    return authFetch<DirectorShot>(`/director/shots/${shotId}/reuse-keyframe`, { method: 'POST' });
  },
  /** 切换分镜 mode（keyframe / frame_sequence / trinity_cards） */
  changeShotMode(shotId: string, mode: DirectorShotMode) {
    return authFetch<DirectorShot>(`/director/shots/${shotId}/change-mode`, {
      method: 'POST',
      body: JSON.stringify({ mode }),
    });
  },
  deleteShot(shotId: string) {
    return authFetch<void>(`/director/shots/${shotId}`, { method: 'DELETE' });
  },
  listTrinityCards(shotId: string) {
    return authFetch<TrinityCardItem[]>(`/director/shots/${shotId}/trinity-cards`);
  },
  generateTrinityCards(
    shotId: string,
    input: TrinityCardsGenerateInput & { forceOverwriteDraft?: boolean }
  ) {
    return authFetch<TrinityCardsTask>(`/director/shots/${shotId}/trinity-cards/generate`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  previewGenerateTrinityCards(
    shotId: string,
    input: TrinityCardsGenerateInput & { targetSlot?: TrinityCardSlot }
  ) {
    return authFetch<TrinityCardsGeneratePreview>(
      `/director/shots/${shotId}/trinity-cards/generate/preview`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      }
    );
  },
  resolveTrinityReferenceAssets(shotId: string, input: TrinityCardsGenerateInput) {
    return authFetch<TrinityReferenceAssetResolution>(
      `/director/shots/${shotId}/trinity-cards/reference-assets/resolve`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      }
    );
  },
  getCurrentTrinityCardsTask(shotId: string) {
    return authFetch<TrinityCardsTask | null>(`/director/shots/${shotId}/trinity-cards/tasks/current`);
  },
  getTrinityCardsTask(shotId: string, taskId: string) {
    return authFetch<TrinityCardsTask>(`/director/shots/${shotId}/trinity-cards/tasks/${taskId}`);
  },
  confirmTrinityCardsTask(
    shotId: string,
    taskId: string,
    prompts: TrinityCardsDraftPrompts
  ) {
    return authFetch<TrinityCardsTask>(
      `/director/shots/${shotId}/trinity-cards/tasks/${taskId}/confirm`,
      {
        method: 'POST',
        body: JSON.stringify({ prompts }),
      }
    );
  },
  cancelTrinityCardsTask(shotId: string, taskId: string) {
    return authFetch<TrinityCardsTask>(
      `/director/shots/${shotId}/trinity-cards/tasks/${taskId}/cancel`,
      { method: 'POST' }
    );
  },
  abortTrinityCardsTask(shotId: string, taskId: string) {
    return authFetch<TrinityCardsTask>(
      `/director/shots/${shotId}/trinity-cards/tasks/${taskId}/abort`,
      { method: 'POST' }
    );
  },

  /**
   * SSE 订阅三合一卡任务的流式事件。
   * 返回 AbortController，调用方可以通过 .abort() 断开连接。
   */
  subscribeTrinityTaskStream(
    shotId: string,
    onEvent: (event: {
      type: 'update' | 'done' | 'error';
      task?: TrinityCardsTask;
      error?: string;
    }) => void
  ): AbortController {
    const ctrl = new AbortController();

    (async () => {
      try {
        const response = await platformFetch(
          `/director/shots/${shotId}/trinity-cards/tasks/stream`,
          { signal: ctrl.signal }
        );
        if (!response.ok || !response.body) {
          onEvent({ type: 'error', error: `SSE 连接失败 (${response.status})` });
          return;
        }
        await readSSEStream(response, (raw) => {
          // 该端点从不发送 [DONE]，防御性跳过；事件对象整体即 JSON payload
          if (raw.trim() === '[DONE]') return;
          try {
            onEvent(JSON.parse(raw));
          } catch {
            // 忽略解析失败的行
          }
        });
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          onEvent({ type: 'error', error: (err as Error).message });
        }
      }
    })();

    return ctrl;
  },
  regenerateTrinityCard(
    shotId: string,
    slot: TrinityCardSlot,
    input: TrinityCardsGenerateInput & { forceOverwriteDraft?: boolean }
  ) {
    return authFetch<TrinityCardsTask>(`/director/shots/${shotId}/trinity-cards/${slot}/regenerate`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  deleteTrinityCard(shotId: string, slot: TrinityCardSlot) {
    return authFetch<{ slot: TrinityCardSlot; deleted: boolean }>(
      `/director/shots/${shotId}/trinity-cards/${slot}`,
      { method: 'DELETE' }
    );
  },
  /** 锁定某个候选图为活跃图 */
  lockTrinityCardCandidate(shotId: string, slot: TrinityCardSlot, imageJobId: string) {
    return authFetch<{ slot: TrinityCardSlot; lockedImageJobId: string }>(
      `/director/shots/${shotId}/trinity-cards/${slot}/lock`,
      { method: 'POST', body: JSON.stringify({ imageJobId }) }
    );
  },
  /** 删除单个候选图 */
  deleteTrinityCardCandidate(shotId: string, slot: TrinityCardSlot, imageJobId: string) {
    return authFetch<{ imageJobId: string; deleted: boolean }>(
      `/director/shots/${shotId}/trinity-cards/${slot}/candidates/${imageJobId}`,
      { method: 'DELETE' }
    );
  },
  previewRegenerateTrinityCardImageOnly(
    shotId: string,
    slot: TrinityCardSlot,
    input?: { modelId?: string; quality?: 'low' | 'medium' | 'high' }
  ) {
    return authFetch<{
      shotId: string;
      slot: TrinityCardSlot;
      label: string;
      modelId: string;
      quality: 'low' | 'medium' | 'high';
      mode: 'text2img' | 'img2img';
      referenceCount: number;
      estimatedCredits: number;
      currentCredits: number;
      prompt: string;
    }>(`/director/shots/${shotId}/trinity-cards/${slot}/regenerate-image-only/preview`, {
      method: 'POST',
      body: JSON.stringify(input ?? {}),
    });
  },
  regenerateTrinityCardImageOnly(
    shotId: string,
    slot: TrinityCardSlot,
    input?: { modelId?: string; quality?: 'low' | 'medium' | 'high' }
  ) {
    return authFetch<{
      slot: TrinityCardSlot;
      jobId: string;
      imageCreditsUsed: number;
    }>(`/director/shots/${shotId}/trinity-cards/${slot}/regenerate-image-only`, {
      method: 'POST',
      body: JSON.stringify(input ?? {}),
    });
  },
  /** 单卡文案生成：仅调用 LLM 生成指定 slot 的提示词，不级联其他 slot */
  generateSingleTrinityCardPrompt(
    shotId: string,
    slot: TrinityCardSlot,
    input: TrinityCardsGenerateInput & { forceOverwriteDraft?: boolean }
  ) {
    return authFetch<TrinityCardsTask>(`/director/shots/${shotId}/trinity-cards/${slot}/generate-prompt`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  /** 单卡图片生成预估 */
  previewGenerateSingleTrinityCardImage(
    shotId: string,
    slot: TrinityCardSlot,
    input?: { modelId?: string; quality?: 'low' | 'medium' | 'high' }
  ) {
    return authFetch<{
      shotId: string;
      slot: TrinityCardSlot;
      label: string;
      modelId: string;
      quality: 'low' | 'medium' | 'high';
      mode: 'text2img' | 'img2img';
      referenceCount: number;
      estimatedCredits: number;
      currentCredits: number;
      prompt: string;
    }>(`/director/shots/${shotId}/trinity-cards/${slot}/generate-image/preview`, {
      method: 'POST',
      body: JSON.stringify(input ?? {}),
    });
  },
  /** 单卡图片生成执行 */
  generateSingleTrinityCardImage(
    shotId: string,
    slot: TrinityCardSlot,
    input?: { modelId?: string; quality?: 'low' | 'medium' | 'high' }
  ) {
    return authFetch<{
      slot: TrinityCardSlot;
      jobId: string;
      imageCreditsUsed: number;
    }>(`/director/shots/${shotId}/trinity-cards/${slot}/generate-image`, {
      method: 'POST',
      body: JSON.stringify(input ?? {}),
    });
  },
  /** 单卡保存提示词 */
  saveTrinityCardPrompt(shotId: string, slot: TrinityCardSlot, prompt: string) {
    return authFetch<{
      slot: TrinityCardSlot;
      prompt: string;
    }>(`/director/shots/${shotId}/trinity-cards/${slot}/save-prompt`, {
      method: 'POST',
      body: JSON.stringify({ prompt }),
    });
  },

  // ---------------- 角色 ----------------

  listCharacters(includeArchived = false) {
    return authFetch<DirectorCharacter[]>(
      `/director/characters?includeArchived=${includeArchived}`
    );
  },
  getAngleTemplates() {
    return authFetch<Array<{ name: string; promptSuffix: string }>>(`/director/characters/templates`);
  },
  createCharacter(input: { name: string; description?: string; portraitImageJobId?: string }) {
    return authFetch<DirectorCharacter>('/director/characters', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  getCharacter(characterId: string) {
    return authFetch<DirectorCharacter>(`/director/characters/${characterId}`);
  },
  updateCharacter(characterId: string, patch: { name?: string; description?: string | null; portraitImageJobId?: string | null }) {
    return authFetch<DirectorCharacter>(`/director/characters/${characterId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },
  deleteCharacter(characterId: string) {
    return authFetch<void>(`/director/characters/${characterId}`, { method: 'DELETE' });
  },
  generateCharacterImage(characterId: string, input: {
    prompt: string;
    modelId: string;
    angleTemplateName?: string;
    size?: string;
    quality?: string;
    /** 可选：参考图（已存在的 ImageJob ID）。命中时切到 img2img 模式 */
    referenceImageJobId?: string;
  }) {
    return authFetch<{ jobId: string; creditsUsed: number }>(
      `/director/characters/${characterId}/generate`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      }
    );
  },
  /** 上传一张本地图作为角色定妆照（multipart/form-data，字段名 file） */
  async uploadCharacterPortrait(characterId: string, file: File) {
    const fd = new FormData();
    fd.append('file', file);
    return authFetchFormData<{ jobId: string }>(
      `/director/characters/${characterId}/upload`,
      fd
    );
  },
  listCharacterImages(characterId: string) {
    return authFetch<DirectorImageJob[]>(`/director/characters/${characterId}/images`);
  },
  addCharacterToProject(projectId: string, characterId: string) {
    return authFetch<void>(`/director/projects/${projectId}/characters/${characterId}`, {
      method: 'POST',
    });
  },
  removeCharacterFromProject(projectId: string, characterId: string) {
    return authFetch<void>(`/director/projects/${projectId}/characters/${characterId}`, {
      method: 'DELETE',
    });
  },
  /**
   * 「从项目移除」前的预览：返回有多少分镜引用了本角色
   */
  previewRemoveCharacterFromProject(projectId: string, characterId: string) {
    return authFetch<{
      shotCount: number;
      shots: Array<{ id: string; title: string | null; shotOrderInProject: number }>;
    }>(`/director/projects/${projectId}/characters/${characterId}/remove-preview`);
  },

  // ---------------- 场景（用户级 + 项目关联，与角色 / 物品同模式） ----------------

  /** 列出用户的所有场景（"从场景库挑选"用） */
  listMyScenes(includeArchived = false) {
    return authFetch<DirectorScene[]>(`/director/scenes?includeArchived=${includeArchived}`);
  },
  /** 列出项目内已加入的场景（按项目内 sortOrder 排序） */
  listScenes(projectId: string) {
    return authFetch<DirectorScene[]>(`/director/projects/${projectId}/scenes`);
  },
  getScene(sceneId: string) {
    return authFetch<DirectorScene>(`/director/scenes/${sceneId}`);
  },
  /**
   * 兼容旧前端：新建场景并立刻加入指定项目
   * 后端实现 = createScene（用户级）+ addSceneToProject 一气呵成
   */
  createScene(projectId: string, input: { name: string; description?: string; settingImageJobId?: string }) {
    return authFetch<DirectorScene>(`/director/projects/${projectId}/scenes`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  /** 创建用户级场景（不绑定项目） */
  createSceneStandalone(input: { name: string; description?: string; settingImageJobId?: string }) {
    return authFetch<DirectorScene>('/director/scenes', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  updateScene(sceneId: string, patch: { name?: string; description?: string | null; settingImageJobId?: string | null }) {
    return authFetch<DirectorScene>(`/director/scenes/${sceneId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },
  deleteScene(sceneId: string) {
    return authFetch<void>(`/director/scenes/${sceneId}`, { method: 'DELETE' });
  },
  /** 把已存在的场景加入项目 */
  addSceneToProject(projectId: string, sceneId: string) {
    return authFetch<void>(`/director/projects/${projectId}/scenes/${sceneId}`, {
      method: 'POST',
    });
  },
  /** 从项目移除场景（仅断关联，不删资产） */
  removeSceneFromProject(projectId: string, sceneId: string) {
    return authFetch<void>(`/director/projects/${projectId}/scenes/${sceneId}`, {
      method: 'DELETE',
    });
  },
  /** 「从项目移除场景」前的预览：返回有多少分镜引用了本场景 */
  previewRemoveSceneFromProject(projectId: string, sceneId: string) {
    return authFetch<{
      shotCount: number;
      shots: Array<{ id: string; title: string | null; shotOrderInProject: number }>;
    }>(`/director/projects/${projectId}/scenes/${sceneId}/remove-preview`);
  },
  generateSceneImage(sceneId: string, input: {
    prompt: string;
    modelId: string;
    angleTemplateName?: string;
    size?: string;
    quality?: string;
    referenceImageJobId?: string;
  }) {
    return authFetch<{ jobId: string; creditsUsed: number }>(
      `/director/scenes/${sceneId}/generate`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      }
    );
  },
  /** 上传一张本地图作为场景设定图 */
  async uploadSceneSetting(sceneId: string, file: File) {
    const fd = new FormData();
    fd.append('file', file);
    return authFetchFormData<{ jobId: string }>(
      `/director/scenes/${sceneId}/upload`,
      fd
    );
  },
  getSceneAngleTemplates() {
    return authFetch<Array<{ name: string; promptSuffix: string }>>(`/director/scenes/templates`);
  },
  listSceneImages(sceneId: string) {
    return authFetch<DirectorImageJob[]>(`/director/scenes/${sceneId}/images`);
  },

  // ---------------- 物品（道具，用户级） ----------------

  listProps(includeArchived = false) {
    return authFetch<DirectorProp[]>(`/director/props?includeArchived=${includeArchived}`);
  },
  getPropAngleTemplates() {
    return authFetch<Array<{ name: string; promptSuffix: string }>>(`/director/props/templates`);
  },
  createProp(input: { name: string; description?: string; portraitImageJobId?: string }) {
    return authFetch<DirectorProp>('/director/props', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  getProp(propId: string) {
    return authFetch<DirectorProp>(`/director/props/${propId}`);
  },
  updateProp(propId: string, patch: { name?: string; description?: string | null; portraitImageJobId?: string | null }) {
    return authFetch<DirectorProp>(`/director/props/${propId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },
  deleteProp(propId: string) {
    return authFetch<void>(`/director/props/${propId}`, { method: 'DELETE' });
  },
  generatePropImage(propId: string, input: {
    prompt: string;
    modelId: string;
    angleTemplateName?: string;
    size?: string;
    quality?: string;
    referenceImageJobId?: string;
  }) {
    return authFetch<{ jobId: string; creditsUsed: number }>(
      `/director/props/${propId}/generate`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      }
    );
  },
  /** 上传一张本地图作为物品主图 */
  async uploadPropPortrait(propId: string, file: File) {
    const fd = new FormData();
    fd.append('file', file);
    return authFetchFormData<{ jobId: string }>(
      `/director/props/${propId}/upload`,
      fd
    );
  },
  listPropImages(propId: string) {
    return authFetch<DirectorImageJob[]>(`/director/props/${propId}/images`);
  },
  addPropToProject(projectId: string, propId: string) {
    return authFetch<void>(`/director/projects/${projectId}/props/${propId}`, {
      method: 'POST',
    });
  },
  removePropFromProject(projectId: string, propId: string) {
    return authFetch<void>(`/director/projects/${projectId}/props/${propId}`, {
      method: 'DELETE',
    });
  },
  /** 「从项目移除物品」前的预览 */
  previewRemovePropFromProject(projectId: string, propId: string) {
    return authFetch<{
      shotCount: number;
      shots: Array<{ id: string; title: string | null; shotOrderInProject: number }>;
    }>(`/director/projects/${projectId}/props/${propId}/remove-preview`);
  },

  // ---------------- Keyframe ----------------

  generateKeyframe(shotId: string, input: {
    prompt: string;
    /** 注入的角色 ID 列表（后端会解析为参考图） */
    injectedCharacterIds?: string[];
    /** 注入的物品 ID 列表（后端解析其主图为参考图，编号紧接角色之后） */
    injectedPropIds?: string[];
    /** 注入的场景 ID（后端会解析其设定图为参考图） */
    injectedSceneId?: string;
    /** 用户显式指定的图片模型；不传走项目级 fallback */
    modelId?: string;
    size?: string;
    quality?: string;
  }) {
    return authFetch<{ jobId: string; creditsUsed: number }>(
      `/director/shots/${shotId}/keyframes/generate`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      }
    );
  },
  previewKeyframe(shotId: string, input: {
    injectedCharacterIds?: string[];
    injectedPropIds?: string[];
    injectedSceneId?: string;
    modelId?: string;
    size?: string;
    quality?: string;
  }) {
    return authFetch<{
      estimatedCredits: number;
      modelId: string;
      modelName: string;
      mode: 'text2img' | 'img2img';
      size: string;
      quality: string;
      /** 实际可用的参考图数量（角色 + 物品 + 场景，按解析后有效条目计） */
      referenceImageCount: number;
      /** 当前模型 img2img 渠道的参考图上限（text2img 模式恒为 0） */
      maxReferenceImageCount: number;
      /** referenceImageCount > maxReferenceImageCount → 阻止生成 */
      exceedsReferenceLimit: boolean;
      /** 当前模型是否配置了 img2img 渠道（mode=img2img 时若为 false 也应阻止生成） */
      hasImg2ImgChannel: boolean;
    }>(`/director/shots/${shotId}/keyframes/preview`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  listKeyframes(shotId: string, params: { limit?: number; includeHidden?: boolean } = {}) {
    const query = new URLSearchParams();
    if (params.limit) query.set('limit', String(params.limit));
    if (params.includeHidden) query.set('includeHidden', 'true');
    return authFetch<{ jobs: DirectorImageJob[]; hasMore: boolean }>(
      `/director/shots/${shotId}/keyframes?${query.toString()}`
    );
  },
  lockKeyframe(shotId: string, imageJobId: string) {
    return authFetch<void>(`/director/shots/${shotId}/keyframes/${imageJobId}/lock`, {
      method: 'POST',
    });
  },
  unlockKeyframe(shotId: string) {
    return authFetch<void>(`/director/shots/${shotId}/keyframes/unlock`, {
      method: 'POST',
    });
  },
  deleteKeyframe(imageJobId: string) {
    return authFetch<void>(`/director/keyframes/${imageJobId}`, { method: 'DELETE' });
  },

  // ---------------- 帧序列（image_sequence 模式） ----------------

  generateFrame(shotId: string, input: {
    prompt: string;
    injectedCharacterIds?: string[];
    injectedPropIds?: string[];
    injectedSceneId?: string;
    modelId?: string;
    size?: string;
    quality?: string;
  }) {
    return authFetch<{ jobId: string; creditsUsed: number }>(
      `/director/shots/${shotId}/frames/generate`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      }
    );
  },
  previewFrame(shotId: string, input: {
    modelId?: string;
    size?: string;
    quality?: string;
  }) {
    return authFetch<{
      estimatedCredits: number;
      modelId: string;
      modelName: string;
      size: string;
      quality: string;
    }>(`/director/shots/${shotId}/frames/preview`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  previewBatchGenerateFrames(shotId: string, input: {
    count: number;
    modelId?: string;
    size?: string;
    quality?: string;
  }) {
    return authFetch<{
      perFrameCredits: number;
      totalCredits: number;
      count: number;
      modelId: string;
      modelName: string;
      size: string;
      quality: string;
      willUseImg2Img: boolean;
      /** 当前序列已有帧数 */
      currentSequenceLength: number;
      /** 视频模型 maxReferenceImageCount；null 表示无法判断（按无上限） */
      videoMaxReferenceImageCount: number | null;
      /** 还能再加多少帧到序列（已综合 BATCH_FRAME_LIMIT 与视频模型上限） */
      remainingSlots: number;
      /** 图片模型 img2img 参考图上限（前端用于校验各帧 anchor + @ 资产数）；
       *  当模型不支持 img2img 时为 null（前端据此禁止 @ 资产） */
      imageMaxReferenceImageCount: number | null;
    }>(`/director/shots/${shotId}/frames/batch-preview`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  batchGenerateFrames(shotId: string, input: {
    /** 锚定参考图 ID；可选（不选时为 null，纯走 @ 资产 → img2img，或 text2img） */
    anchorImageJobId: string | null;
    /** 各帧独立的 prompt + 资产引用（@ 解析结果由前端在 MentionTextarea 中得到） */
    prompts: Array<{
      text: string;
      characterIds: string[];
      propIds: string[];
      sceneId: string | null;
    }>;
    modelId?: string;
    size?: string;
    quality?: string;
    appendToSequence?: boolean;
  }) {
    return authFetch<{
      jobIds: string[];
      creditsUsed: number;
      fellBackToText2Img: boolean;
    }>(`/director/shots/${shotId}/frames/batch-generate`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  /**
   * AI 多帧 prompt 助手：把一句话剧本拆成 N 帧 prompt（不写库）。
   *
   * 后端会扣费、调 LLM、失败自动退款。返回的 frames 直接灌进批量弹窗的 8 个输入框。
   * AI 仅负责文字；模型/画质/锚定图等参数仍由用户自行选择。
   * videoGuidance 是分镜级的视频指导（动作 / 节奏 / 过渡 / 镜头运动），可写回 shot.prompt。
   */
  aiStoryboardForShot(shotId: string, input: {
    scriptText: string;
    frameCount: number;
    anchorImageJobId?: string | null;
  }) {
    return authFetch<{
      frames: Array<{
        text: string;
        characterIds: string[];
        propIds: string[];
        sceneId: string | null;
      }>;
      videoGuidance: string;
      creditsUsed: number;
    }>(`/director/shots/${shotId}/frames/ai-storyboard`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  listShotFrames(shotId: string, params: { limit?: number } = {}) {
    const query = new URLSearchParams();
    if (params.limit) query.set('limit', String(params.limit));
    return authFetch<{
      sequence: Array<{
        id: string;
        sortOrder: number;
        image: DirectorImageJob;
      }>;
      candidates: DirectorImageJob[];
      hasMore: boolean;
      /** 视频模型 maxReferenceImageCount；null 表示未配置或无法判断 */
      videoMaxReferenceImageCount: number | null;
    }>(`/director/shots/${shotId}/frames?${query.toString()}`);
  },
  addFrameToSequence(shotId: string, imageJobId: string) {
    return authFetch<void>(
      `/director/shots/${shotId}/frames/${imageJobId}/add`,
      { method: 'POST' }
    );
  },
  removeFrameFromSequence(shotId: string, imageJobId: string) {
    return authFetch<void>(
      `/director/shots/${shotId}/frames/${imageJobId}/remove`,
      { method: 'POST' }
    );
  },
  reorderSequence(shotId: string, orderedImageJobIds: string[]) {
    return authFetch<void>(`/director/shots/${shotId}/frames/reorder`, {
      method: 'POST',
      body: JSON.stringify({ orderedImageJobIds }),
    });
  },
  deleteFrame(imageJobId: string) {
    return authFetch<void>(`/director/frames/${imageJobId}`, { method: 'DELETE' });
  },

  activateShot(shotId: string) {
    return authFetch<{ videoJobId: string; status: string; creditsUsed: number }>(
      `/director/shots/${shotId}/activate`,
      {
        method: 'POST',
      }
    );
  },
  reactivateShot(shotId: string) {
    return authFetch<{ videoJobId: string; status: string; creditsUsed: number }>(
      `/director/shots/${shotId}/reactivate`,
      {
        method: 'POST',
      }
    );
  },
  deactivateShot(shotId: string) {
    return authFetch<{ cancelledJobId: string | null; refunded: boolean; status: string }>(
      `/director/shots/${shotId}/deactivate`,
      { method: 'POST' }
    );
  },

  // ---------------- 视频候选池 ----------------

  /**
   * 列出本分镜所有可见视频任务（active + 历史候选，按 createdAt desc）。
   * 一般情况下用 getProject 返回的 candidateVideos 即可，本接口用于 hover 时刷新单个分镜的候选池。
   */
  listVideoCandidates(shotId: string) {
    return authFetch<
      Array<{
        id: string;
        isActive: boolean;
        status: string;
        resultStorageKey: string | null;
        resultStorageType: string | null;
        coverStorageKey: string | null;
        resultDuration: number | null;
        duration: number;
        createdAt: string;
      }>
    >(`/director/shots/${shotId}/video-candidates`);
  },

  /** 把指定候选切换为 active（不扣费，原 active 自动变候选） */
  useVideoCandidate(shotId: string, videoJobId: string) {
    return authFetch<void>(
      `/director/shots/${shotId}/video-candidates/${videoJobId}/use`,
      { method: 'POST' }
    );
  },

  /** 软删候选视频（active 不能软删，需先切到其他候选） */
  hideVideoCandidate(shotId: string, videoJobId: string) {
    return authFetch<void>(
      `/director/shots/${shotId}/video-candidates/${videoJobId}/hide`,
      { method: 'POST' }
    );
  },
  previewActivateAll(projectId: string) {
    return authFetch<ActivateAllPreview>(
      `/director/projects/${projectId}/activate-all/preview`,
      { method: 'POST' }
    );
  },
  previewActivateShot(shotId: string) {
    return authFetch<{
      estimatedCredits: number;
      modelId: string;
      duration: number;
      resolution: string;
    }>(`/director/shots/${shotId}/activate/preview`, { method: 'POST' });
  },
  activateAll(projectId: string) {
    return authFetch<ActivateAllPreview & {
      videoJobIds: string[];
      queuedCount: number;
      creditsUsed: number;
    }>(`/director/projects/${projectId}/activate-all`, {
      method: 'POST',
    });
  },

  // ---------------- 成片合成 ----------------

  createComposition(projectId: string, settings: {
    transitionType?: 'none' | 'fade';
    transitionDurationMs?: number;
    outputResolution?: 'auto' | '720p' | '1080p';
    watermarkEnabled?: boolean;
    audioNormalizeEnabled?: boolean;
  } = {}) {
    return authFetch<DirectorComposition>(
      `/director/projects/${projectId}/compositions`,
      {
        method: 'POST',
        body: JSON.stringify(settings),
      }
    );
  },
  listCompositions(projectId: string) {
    return authFetch<DirectorComposition[]>(
      `/director/projects/${projectId}/compositions`
    );
  },
  getComposition(compositionId: string) {
    return authFetch<DirectorComposition>(`/director/compositions/${compositionId}`);
  },

  // ---------------- 分享 ----------------

  createShare(projectId: string, input: {
    password?: string;
    expiresInDays?: 'forever' | number;
    allowDownload?: boolean;
  } = {}) {
    return authFetch<{ share: DirectorShare; shareUrl: string }>(
      `/director/projects/${projectId}/share`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      }
    );
  },
  revokeShare(projectId: string) {
    return authFetch<void>(`/director/projects/${projectId}/share`, { method: 'DELETE' });
  },

  // ---------------- 拆分镜 ----------------

  splitScript(projectId: string, scriptText: string) {
    return authFetch<{ shots: ShotProposal[]; creditsUsed: number }>(
      `/director/projects/${projectId}/shot-split`,
      {
        method: 'POST',
        body: JSON.stringify({ scriptText }),
      }
    );
  },
  applyProposals(projectId: string, proposals: ShotProposal[]) {
    return authFetch<DirectorShot[]>(
      `/director/projects/${projectId}/shot-split/apply`,
      {
        method: 'POST',
        body: JSON.stringify({ proposals }),
      }
    );
  },
  splitByLine(projectId: string, scriptText: string) {
    return authFetch<DirectorShot[]>(
      `/director/projects/${projectId}/shot-split/by-line`,
      {
        method: 'POST',
        body: JSON.stringify({ scriptText }),
      }
    );
  },

  // ---------------- 作品库导入（spec 6.3 / 6.4 / 6.5）----------------

  /**
   * 列出当前用户可导入的非导演图片（来自主页图片生成 / 收藏等），用于 ImportFromLibraryDialog 选图。
   * 支持按 model / size 筛选。
   */
  listLibraryImages(params: { page?: number; pageSize?: number; model?: string; size?: string } = {}) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.pageSize) query.set('pageSize', String(params.pageSize));
    if (params.model) query.set('model', params.model);
    if (params.size) query.set('size', params.size);
    return authFetch<{
      data: Array<{
        id: string;
        prompt: string;
        model: string;
        size: string | null;
        resultUrl: string | null;
        thumbnailUrl: string | null;
        width: number | null;
        height: number | null;
        createdAt: string;
      }>;
      pagination: { page: number; pageSize: number; total: number };
    }>(`/director/library/images?${query.toString()}`);
  },
  importToCharacter(characterId: string, sourceImageJobId: string) {
    return authFetch<DirectorImageJob>(`/director/library/import/character`, {
      method: 'POST',
      body: JSON.stringify({ characterId, sourceImageJobId }),
    });
  },
  importToScene(sceneId: string, sourceImageJobId: string) {
    return authFetch<DirectorImageJob>(`/director/library/import/scene`, {
      method: 'POST',
      body: JSON.stringify({ sceneId, sourceImageJobId }),
    });
  },
  importToShotKeyframe(shotId: string, sourceImageJobId: string) {
    return authFetch<DirectorImageJob>(`/director/library/import/shot-keyframe`, {
      method: 'POST',
      body: JSON.stringify({ shotId, sourceImageJobId }),
    });
  },
  importToProp(propId: string, sourceImageJobId: string) {
    return authFetch<DirectorImageJob>(`/director/library/import/prop`, {
      method: 'POST',
      body: JSON.stringify({ propId, sourceImageJobId }),
    });
  },
  importToTrinityCardCandidate(shotId: string, slot: TrinityCardSlot, sourceImageJobId: string) {
    return authFetch<DirectorImageJob>(`/director/library/import/trinity-card-candidate`, {
      method: 'POST',
      body: JSON.stringify({ shotId, slot, sourceImageJobId }),
    });
  },
};
